import { useEffect } from 'react';

// Dialerr embeddable softphone — loaded ONLY inside the admin panel (mounted by
// AdminLayout when an admin is logged in), so it never appears on the patient,
// phleb, or lab portals. Uses the official hosted embed.js with the publishable
// key (pk_...), which is client-side safe like a Stripe publishable key. The
// secret key (sk_...) is never used here — it belongs only on the server.
//
// Set the key in the environment (client build var):
//   REACT_APP_DIALERR_KEY=pk_live_...
// in .env locally and in the Vercel project settings. With no key set, this
// renders nothing.
const DIALERR_KEY = process.env.REACT_APP_DIALERR_KEY;
const DIALERR_BASE = process.env.REACT_APP_DIALERR_BASE || 'https://dialerr.com';
const SCRIPT_ID = 'dialerr-embed';

export default function DialerrWidget() {
  useEffect(() => {
    if (!DIALERR_KEY || !String(DIALERR_KEY).startsWith('pk_')) {
      // Most common cause of "I don't see the softphone": the key wasn't in the
      // build. CRA inlines REACT_APP_* at build time — set it in Vercel and
      // REDEPLOY (a new build), not just add the env var.
      console.warn('[Dialerr] Softphone disabled — REACT_APP_DIALERR_KEY is not set at build time (or is not a pk_ key). Set it in Vercel env and redeploy.');
      return undefined;
    }
    if (document.getElementById(SCRIPT_ID)) return undefined; // already loaded

    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = `${DIALERR_BASE.replace(/\/$/, '')}/embed.js`;
    s.async = true;
    s.setAttribute('data-key', DIALERR_KEY);
    s.onload = () => console.info('[Dialerr] embed.js loaded — launcher should appear bottom-right.');
    s.onerror = () => console.error('[Dialerr] Failed to load embed.js from ' + DIALERR_BASE + ' — check the URL / network / key domain restrictions.');
    // Seamless per-admin sign-in: the widget fetches a short-lived token from
    // our route, which reads the signed session cookie set at admin login and
    // mints a token for that admin's Dialerr agent. Same-origin, so the cookie
    // is sent automatically.
    s.setAttribute('data-token-url', '/api/dialerr/token');
    document.body.appendChild(s);

    // Tear down when leaving the admin panel so the softphone doesn't linger on
    // other portals: remove the loader script, ask the widget to self-destruct
    // if it exposes an API, and sweep any launcher/iframe it injected.
    return () => {
      try { window.Dialerr?.destroy?.(); } catch (e) { /* ignore */ }
      document.getElementById(SCRIPT_ID)?.remove();
      document
        .querySelectorAll('[id^="dialerr-"], [class^="dialerr-"], iframe[src*="dialerr.com"]')
        .forEach((el) => { if (el.id !== SCRIPT_ID) el.remove(); });
      try { delete window.Dialerr; } catch (e) { window.Dialerr = undefined; }
    };
  }, []);

  return null;
}
